import type { Api } from "../../../services/api";

export type RunDebug =
  Api["get"]["/assistant/conversations/:conversationId/runs/:runId/debug"]["output"]["body"];
export type RunConfig =
  Api["get"]["/assistant/conversations/:conversationId/runs/:runId/debug/config"]["output"]["body"];
type Round = RunDebug["rounds"][number];
type Call = RunDebug["calls"][number];
export type MissionSearch = Exclude<Call["search"], null>;
type Mission = RunDebug["missions"][number];
type EventKind = RunDebug["events"][number]["kind"];

// Alert thresholds.
const SLOW_ROUND_MS = 20_000;
const SLOW_TOOL_MS = 10_000;
const MISSION_WAIT_MS = 10_000;
const LARGE_OUTPUT_CHARS = 20_000;
const PROMPT_WINDOW_SHARE = 0.8;
const CACHE_DROP = 0.3;
// A dead worker leaves its rows open; past this delay after the run's end, they are lost.
const LOST_AFTER_MS = 60_000;

export function isSettled(status: RunDebug["run"]["status"]): boolean {
  switch (status) {
    case "cancelled":
    case "completed":
    case "replied":
    case "failed":
      return true;
    case "queued":
    case "running":
    case "waiting":
      return false;
    default:
      throw new Error(status satisfies never);
  }
}

// The last rows close, and the debug copy is built, after the run settles, so polling waits for them, up to the lost delay.
export function pollInterval(debug: RunDebug): number | false {
  switch (debug.run.status) {
    case "queued":
    case "running":
      return 1000;
    // The run sleeps until the reader answers its card.
    case "waiting":
      return 5000;
    case "cancelled":
    case "completed":
    case "replied":
    case "failed": {
      if (debug.run.finishedAt === null)
        throw new Error("should not happen: settled run without an end");
      const open =
        debug.rounds.some((round) => round.outcome === null) ||
        debug.calls.some((call) => call.finishedAt === null) ||
        debug.filesystem === null ||
        debug.filesystem.status === "pending";
      return open &&
        Date.parse(debug.serverTime) - Date.parse(debug.run.finishedAt) <=
          LOST_AFTER_MS
        ? 1000
        : false;
    }
    default:
      throw new Error(debug.run.status satisfies never);
  }
}

export type RowState = "running" | "lost" | "done" | "failed";

export type Alert =
  | { kind: "slow-round"; ms: number }
  | { kind: "slow-tool"; ms: number }
  | { kind: "mission-wait"; ms: number }
  | { kind: "large-output"; chars: number }
  | { kind: "prompt-near-window"; share: number }
  | { kind: "cache-drop"; from: number; to: number }
  | { kind: "repeated-command"; occurrence: number }
  | { kind: "exit-code"; code: number }
  | { kind: "empty-round" };

export function alertCategory(alert: Alert): "slow" | "context" | "waste" {
  switch (alert.kind) {
    case "slow-round":
    case "slow-tool":
    case "mission-wait":
      return "slow";
    case "large-output":
    case "prompt-near-window":
    case "cache-drop":
      return "context";
    case "repeated-command":
    case "exit-code":
    case "empty-round":
      return "waste";
    default:
      throw new Error(alert satisfies never);
  }
}

export function alertText(alert: Alert): string {
  switch (alert.kind) {
    case "slow-round":
      return `Round took ${formatMs(alert.ms)}`;
    case "slow-tool":
      return `Tool took ${formatMs(alert.ms)}`;
    case "mission-wait":
      return `Waited ${formatMs(alert.ms)} before its first round`;
    case "large-output":
      return `Output of ${formatCount(alert.chars)} characters`;
    case "prompt-near-window":
      return `Prompt fills ${formatPercent(alert.share)} of the window`;
    case "cache-drop":
      return `Cache hit fell from ${formatPercent(alert.from)} to ${formatPercent(alert.to)}`;
    case "repeated-command":
      return `Same command, run ${String(alert.occurrence)} times so far`;
    case "exit-code":
      return `Exit code ${String(alert.code)}`;
    case "empty-round":
      return "Round emitted no text and no tool call";
    default:
      throw new Error(alert satisfies never);
  }
}

// Times are milliseconds since the run's creation.
type Span = { start: number; end: number };

export type TraceCall = Span & { row: Call; state: RowState; alerts: Alert[] };

export type TraceRound = Span & {
  row: Round;
  // 1-based, within its agent.
  index: number;
  state: RowState;
  credits: number;
  calls: TraceCall[];
  alerts: Alert[];
};

type AgentKind =
  | { kind: "intent" }
  | { kind: "clarification" }
  | { kind: "preAnswer" }
  | { kind: "root" }
  | { kind: "mission"; mission: Mission };

type Bucket = {
  key: string;
  attempt: number;
  agent: AgentKind;
  rounds: TraceRound[];
  // Calls that no stored round emitted: the run predates the round rows.
  calls: TraceCall[];
};

export type TraceAgent = Span &
  AgentKind &
  Omit<Bucket, "agent"> & {
    label: string;
    // A mission's time from its creation to its first round, its publication search included.
    wait: Span | null;
    credits: number;
    modelMs: number;
    toolMs: number;
    alerts: Alert[];
  };

export type TraceTarget =
  | { kind: "agent"; key: string }
  | { kind: "round"; id: string }
  | { kind: "call"; id: string };

export type TraceAlert = { alert: Alert; target: TraceTarget; where: string };

export type Trace = {
  now: number;
  end: number;
  queue: number | null;
  agents: TraceAgent[];
  markers: { kind: EventKind; at: number }[];
  alerts: TraceAlert[];
  modelMs: number;
  toolMs: number;
  sources: number;
};

function reportedInputTokens(round: Round): number | null {
  if (round.outcome === null) return null;
  switch (round.outcome.kind) {
    case "completed":
      return round.outcome.inputTokens;
    case "failed":
      return null;
    default:
      throw new Error(round.outcome satisfies never);
  }
}

function cacheHit(round: Round): number | null {
  if (round.outcome === null) return null;
  switch (round.outcome.kind) {
    case "completed":
      return round.outcome.cachedInputTokens === null ||
        round.outcome.inputTokens === 0
        ? null
        : round.outcome.cachedInputTokens / round.outcome.inputTokens;
    case "failed":
      return null;
    default:
      throw new Error(round.outcome satisfies never);
  }
}

function emitsNothing(round: Round): boolean {
  if (round.outcome === null) return false;
  switch (round.outcome.kind) {
    case "completed":
      return round.outcome.content.every((part) => {
        switch (part.type) {
          case "text":
            return part.text.trim() === "";
          case "tool-call":
            return false;
          default:
            throw new Error(part satisfies never);
        }
      });
    case "failed":
      return false;
    default:
      throw new Error(round.outcome satisfies never);
  }
}

// The tool calls a round emitted, in order.
export function emittedToolCalls(round: Round) {
  if (round.outcome === null) return [];
  switch (round.outcome.kind) {
    case "completed":
      return round.outcome.content.flatMap((part) => {
        switch (part.type) {
          case "text":
            return [];
          case "tool-call":
            return [part];
          default:
            throw new Error(part satisfies never);
        }
      });
    case "failed":
      return [];
    default:
      throw new Error(round.outcome satisfies never);
  }
}

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

export function deriveTrace(
  debug: RunDebug,
  config: { limits: Pick<RunConfig["limits"], "contextWindow"> } | null
): Trace {
  const origin = Date.parse(debug.run.createdAt);
  const at = (date: string) => Date.parse(date) - origin;
  const now = at(debug.serverTime);
  const settled = isSettled(debug.run.status);
  if (settled && debug.run.finishedAt === null)
    throw new Error("should not happen: settled run without an end");
  const end = debug.run.finishedAt === null ? now : at(debug.run.finishedAt);
  // An open row is lost once a later attempt took over or the run ended past the delay.
  function openRow(attempt: number): { state: RowState; end: number } {
    return attempt < debug.run.attempt || (settled && now - end > LOST_AFTER_MS)
      ? { state: "lost", end }
      : { state: "running", end: now };
  }

  const buckets = new Map<string, Bucket>();
  function bucket(key: string, attempt: number, agent: AgentKind): Bucket {
    const existing = buckets.get(key);
    if (existing !== undefined) return existing;
    const created: Bucket = { key, attempt, agent, rounds: [], calls: [] };
    buckets.set(key, created);
    return created;
  }
  // A mission is an agent from its creation, before its first round.
  for (const mission of debug.missions)
    bucket(`mission:${mission.id}`, mission.attempt, {
      kind: "mission",
      mission,
    });
  function missionBucket(missionId: string): Bucket {
    const found = buckets.get(`mission:${missionId}`);
    if (found === undefined)
      throw new Error(`should not happen: unknown mission ${missionId}`);
    return found;
  }
  function engineBucket(
    attempt: number,
    kind: "intent" | "clarification" | "preAnswer" | "root"
  ): Bucket {
    return bucket(`${String(attempt)}:${kind}`, attempt, { kind });
  }
  function roundBucket(round: Round): Bucket {
    switch (round.role) {
      case "intent":
      case "clarification":
      case "preAnswer":
        return engineBucket(round.attempt, round.role);
      case "orchestrator":
        return engineBucket(round.attempt, "root");
      case "retrieval":
        if (round.missionId === null)
          throw new Error(
            "should not happen: retrieval round without a mission"
          );
        return missionBucket(round.missionId);
      default:
        throw new Error(round.role satisfies never);
    }
  }
  function callBucket(call: Call): Bucket {
    if (call.scope === "root") return engineBucket(call.attempt, "root");
    if (call.scope === "clarification")
      return engineBucket(call.attempt, "clarification");
    return missionBucket(call.scope);
  }

  const occurrences = new Map<string, number>();
  const calls = debug.calls.map((call): TraceCall => {
    const start = at(call.startedAt);
    const closed =
      call.finishedAt === null
        ? openRow(call.attempt)
        : {
            state:
              call.exitCode !== null && call.exitCode !== 0
                ? ("failed" as const)
                : ("done" as const),
            end: at(call.finishedAt),
          };
    const previous = occurrences.get(call.command);
    const occurrence = previous === undefined ? 1 : previous + 1;
    occurrences.set(call.command, occurrence);
    const alerts: Alert[] = [];
    if (closed.end - start > SLOW_TOOL_MS)
      alerts.push({ kind: "slow-tool", ms: closed.end - start });
    if (call.outputChars !== null && call.outputChars > LARGE_OUTPUT_CHARS)
      alerts.push({ kind: "large-output", chars: call.outputChars });
    if (call.exitCode !== null && call.exitCode !== 0)
      alerts.push({ kind: "exit-code", code: call.exitCode });
    if (occurrence > 1) alerts.push({ kind: "repeated-command", occurrence });
    return { row: call, state: closed.state, start, end: closed.end, alerts };
  });

  const callsById = new Map(calls.map((call) => [call.row.toolCallId, call]));
  const linked = new Set<string>();
  const contextWindow = config === null ? null : config.limits.contextWindow;
  for (const round of debug.rounds) {
    const owner = roundBucket(round);
    const start = at(round.startedAt);
    const closed =
      round.outcome === null
        ? openRow(round.attempt)
        : {
            state:
              round.outcome.kind === "completed"
                ? ("done" as const)
                : ("failed" as const),
            end: at(round.outcome.finishedAt),
          };
    const alerts: Alert[] = [];
    if (closed.end - start > SLOW_ROUND_MS)
      alerts.push({ kind: "slow-round", ms: closed.end - start });
    // The window counts tokens: only a round whose usage the provider reported has a share of it.
    const input = reportedInputTokens(round);
    if (
      contextWindow !== null &&
      input !== null &&
      input > PROMPT_WINDOW_SHARE * contextWindow
    )
      alerts.push({
        kind: "prompt-near-window",
        share: input / contextWindow,
      });
    const hit = cacheHit(round);
    const previousHit = owner.rounds
      .map((earlier) => cacheHit(earlier.row))
      .filter((value) => value !== null)
      .at(-1);
    if (
      hit !== null &&
      previousHit !== undefined &&
      previousHit - hit >= CACHE_DROP
    )
      alerts.push({ kind: "cache-drop", from: previousHit, to: hit });
    if (emitsNothing(round)) alerts.push({ kind: "empty-round" });
    // A refused or unrun tool call has no row.
    const emitted = emittedToolCalls(round)
      .map((part) => callsById.get(part.toolCallId))
      .filter((call) => call !== undefined);
    for (const call of emitted) linked.add(call.row.id);
    owner.rounds.push({
      row: round,
      index: owner.rounds.length + 1,
      state: closed.state,
      start,
      end: closed.end,
      credits: roundCredits(round),
      calls: emitted,
      alerts,
    });
  }
  for (const call of calls)
    if (!linked.has(call.row.id)) callBucket(call.row).calls.push(call);

  const agents = [...buckets.values()].map((owner): TraceAgent => {
    const allCalls = [
      ...owner.rounds.flatMap((round) => round.calls),
      ...owner.calls,
    ];
    const first = owner.rounds.at(0);
    const wait =
      owner.agent.kind === "mission"
        ? {
            start: at(owner.agent.mission.createdAt),
            end:
              first !== undefined
                ? first.start
                : owner.agent.mission.status === "running"
                  ? openRow(owner.agent.mission.attempt).end
                  : at(owner.agent.mission.updatedAt),
          }
        : null;
    const spans: Span[] = [
      ...(wait === null ? [] : [wait]),
      ...owner.rounds,
      ...allCalls,
    ];
    if (spans.length === 0)
      throw new Error(`should not happen: agent ${owner.key} has no span`);
    return {
      ...owner.agent,
      key: owner.key,
      attempt: owner.attempt,
      rounds: owner.rounds,
      calls: owner.calls,
      label: agentLabel(owner.agent, owner.attempt, debug.run.attempt),
      start: Math.min(...spans.map((span) => span.start)),
      end: Math.max(...spans.map((span) => span.end)),
      wait,
      credits:
        sum(owner.rounds.map((round) => round.credits)) +
        sum(allCalls.map((call) => callCredits(call.row))),
      modelMs: sum(owner.rounds.map((round) => round.end - round.start)),
      toolMs: sum(allCalls.map((call) => call.end - call.start)),
      alerts:
        wait !== null && wait.end - wait.start > MISSION_WAIT_MS
          ? [{ kind: "mission-wait", ms: wait.end - wait.start }]
          : [],
    };
  });
  agents.sort((left, right) => left.start - right.start);

  const alerts = agents.flatMap((agent): TraceAlert[] => [
    ...agent.alerts.map((alert) => ({
      alert,
      target: { kind: "agent" as const, key: agent.key },
      where: agent.label,
    })),
    ...agent.rounds.flatMap((round) => [
      ...round.alerts.map((alert) => ({
        alert,
        target: { kind: "round" as const, id: round.row.id },
        where: `${agent.label}, round ${String(round.index)}`,
      })),
      ...round.calls.flatMap((call) =>
        call.alerts.map((alert) => ({
          alert,
          target: { kind: "call" as const, id: call.row.id },
          where: `${agent.label}, round ${String(round.index)}, tool`,
        }))
      ),
    ]),
    ...agent.calls.flatMap((call) =>
      call.alerts.map((alert) => ({
        alert,
        target: { kind: "call" as const, id: call.row.id },
        where: `${agent.label}, tool`,
      }))
    ),
  ]);

  // The first start: a resumed attempt moves `run.startedAt` past the reader's pause.
  const started = debug.events.find((event) => event.kind === "started");
  return {
    now,
    end,
    queue: started === undefined ? null : at(started.createdAt),
    agents,
    markers: debug.events
      .filter((event) => isMilestone(event.kind))
      .map((event) => ({ kind: event.kind, at: at(event.createdAt) })),
    alerts,
    modelMs: sum(agents.map((agent) => agent.modelMs)),
    toolMs: sum(agents.map((agent) => agent.toolMs)),
    sources: debug.calls.filter((call) => call.step !== null).length,
  };
}

function agentLabel(
  agent: AgentKind,
  attempt: number,
  attempts: number
): string {
  const suffix = attempts > 1 ? ` · attempt ${String(attempt)}` : "";
  switch (agent.kind) {
    case "intent":
    case "clarification":
    case "root":
      return `${agent.kind}${suffix}`;
    case "preAnswer":
      return `pre-answer${suffix}`;
    case "mission":
      return `mission ${String(agent.mission.number)} · ${agent.mission.label}${suffix}`;
    default:
      throw new Error(agent satisfies never);
  }
}

// Text deltas and progress lines would bury the lifecycle on the axis.
function isMilestone(kind: EventKind): boolean {
  switch (kind) {
    case "text_delta":
    case "research_progress":
      return false;
    case "clarification_requested":
    case "resumed":
    case "cancelled":
    case "answer_available":
    case "accepted":
    case "started":
    case "research_started":
    case "research_completed":
    case "completed":
    case "replied":
    case "failed":
      return true;
    default:
      throw new Error(kind satisfies never);
  }
}

export function roundCredits(round: Round): number {
  if (round.outcome === null) return 0;
  switch (round.outcome.kind) {
    case "completed":
      return round.outcome.credits;
    case "failed":
      return 0;
    default:
      throw new Error(round.outcome satisfies never);
  }
}

// A mission's juisci search bills its plan and ranking calls; no other tool call costs credits.
export function callCredits(call: Call): number {
  return call.search === null
    ? 0
    : sum(call.search.models.map((model) => model.credits));
}

export function searchOutcomeText(search: MissionSearch): string {
  switch (search.outcome.kind) {
    case "recall_failed":
      return "recall failed";
    case "ranking_failed":
      return `ranking failed after ${String(search.outcome.recall.candidates)} candidates`;
    case "succeeded":
      return `${String(search.outcome.recall.candidates)} candidates, ${String(search.outcome.results)} results (${String(search.outcome.core)} core)`;
    default:
      throw new Error(search.outcome satisfies never);
  }
}

export function roundAlerts(round: TraceRound): Alert[] {
  return [...round.alerts, ...round.calls.flatMap((call) => call.alerts)];
}

export function agentAlerts(agent: TraceAgent): Alert[] {
  return [
    ...agent.alerts,
    ...agent.rounds.flatMap((round) => roundAlerts(round)),
    ...agent.calls.flatMap((call) => call.alerts),
  ];
}

export type TokenCosts = {
  uncachedInput: number;
  cachedInput: number;
  output: number;
  // What the cached tokens would have cost at the uncached rate, minus what they cost.
  cacheSavings: number;
  // Rounds of a model the API has no rate for, left out of the split.
  unpriced: number;
  // The credits of the priced rounds, which differ from the split when a round billed another rate.
  billed: number;
};

// The API's computeCostCents at the config's rates, in dollars and split by token kind.
// The config prices a model at the run's requested tier, so a Fast round served at the default tier billed less.
export function tokenCosts(
  rounds: Round[],
  rates: RunConfig["rates"]
): TokenCosts {
  const costs: TokenCosts = {
    uncachedInput: 0,
    cachedInput: 0,
    output: 0,
    cacheSavings: 0,
    unpriced: 0,
    billed: 0,
  };
  for (const round of rounds) {
    if (round.outcome === null) continue;
    switch (round.outcome.kind) {
      case "completed": {
        const listed = rates.find((rate) => rate.model === round.modelId);
        if (listed === undefined)
          throw new Error(
            `should not happen: no rate entry for ${round.modelId}`
          );
        const rate = listed.usdPerMtok;
        if (rate === null) {
          costs.unpriced += 1;
          break;
        }
        const { inputTokens, cachedInputTokens, outputTokens } = round.outcome;
        const cached =
          cachedInputTokens === null || rate.cachedInput === null
            ? 0
            : Math.min(cachedInputTokens, inputTokens);
        const cachedRate =
          rate.cachedInput === null ? rate.input : rate.cachedInput;
        costs.uncachedInput +=
          ((inputTokens - cached) / 1_000_000) * rate.input;
        costs.cachedInput += (cached / 1_000_000) * cachedRate;
        costs.output += (outputTokens / 1_000_000) * rate.output;
        costs.cacheSavings += (cached / 1_000_000) * (rate.input - cachedRate);
        costs.billed += round.outcome.credits;
        break;
      }
      case "failed":
        break;
      default:
        throw new Error(round.outcome satisfies never);
    }
  }
  return costs;
}

export const COMPOSITION_PARTS = [
  "system",
  "tools",
  "user",
  "assistant",
  "reasoning",
  "toolCalls",
  "toolResults",
] as const satisfies (keyof Round["composition"])[];

// JSON keys and punctuation around the parts.
export function compositionOverhead(round: Round): number {
  return (
    round.promptBytes -
    sum(COMPOSITION_PARTS.map((part) => round.composition[part]))
  );
}

export function firstLine(text: string): string {
  const newline = text.indexOf("\n");
  return newline === -1 ? text : text.slice(0, newline);
}

export function formatMs(ms: number): string {
  if (ms < 1000) return `${String(Math.round(ms))} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  return `${String(Math.floor(seconds / 60))} min ${String(seconds % 60)} s`;
}

export function formatUsd(usd: number): string {
  return `$${usd.toFixed(4)}`;
}

// Credits are cents.
export function formatCredits(credits: number): string {
  return formatUsd(credits / 100);
}

export function formatCount(count: number): string {
  if (count < 1000) return String(count);
  if (count < 1_000_000) return `${(count / 1000).toFixed(1)}k`;
  return `${(count / 1_000_000).toFixed(2)}M`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function formatPercent(share: number): string {
  return `${String(Math.round(share * 100))} %`;
}

export type JsonToken = {
  kind: "key" | "string" | "number" | "literal" | "punctuation" | "plain";
  text: string;
};

const JSON_TOKEN =
  /"(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?|true|false|null|[{}[\],:]/g;

// JSON.stringify puts the colon right after a key.
function jsonTokenKind(token: string, beforeColon: boolean): JsonToken["kind"] {
  if (token.startsWith('"')) return beforeColon ? "key" : "string";
  if (token === "true" || token === "false" || token === "null")
    return "literal";
  if (token.length === 1 && "{}[],:".includes(token)) return "punctuation";
  return "number";
}

// JSON objects and arrays, indented and split for coloring; null keeps any other text as is.
// A string's escaped newlines break the line, under the indentation of the line it starts on.
export function jsonTokens(text: string): JsonToken[] | null {
  let indented: string;
  try {
    const parsed: unknown = JSON.parse(text);
    // A bare scalar, such as a model answer of 42, stays text.
    if (typeof parsed !== "object" || parsed === null) return null;
    // JSON.stringify recurses: a nesting that JSON.parse accepts can overflow the stack.
    indented = JSON.stringify(parsed, null, 2);
  } catch {
    return null;
  }
  return indented.split("\n").flatMap((line, index) => {
    const indent = line.slice(0, line.length - line.trimStart().length);
    const tokens: JsonToken[] =
      index === 0 ? [] : [{ kind: "plain", text: "\n" }];
    let cursor = 0;
    for (const match of line.matchAll(JSON_TOKEN)) {
      const end = match.index + match[0].length;
      if (match.index > cursor)
        tokens.push({ kind: "plain", text: line.slice(cursor, match.index) });
      const kind = jsonTokenKind(match[0], line.charAt(end) === ":");
      tokens.push({
        kind,
        text:
          kind === "string"
            ? match[0].replace(/\\\\|\\n/g, (escape) =>
                escape === "\\n" ? `\n${indent}` : escape
              )
            : match[0],
      });
      cursor = end;
    }
    if (cursor < line.length)
      tokens.push({ kind: "plain", text: line.slice(cursor) });
    return tokens;
  });
}

function stateText(state: RowState): string {
  switch (state) {
    case "running":
      return " (running)";
    case "lost":
      return " (lost)";
    case "done":
    case "failed":
      return "";
    default:
      throw new Error(state satisfies never);
  }
}

function roundLine(round: TraceRound): string {
  const head = `round ${String(round.index)}, ${formatMs(round.end - round.start)}${stateText(round.state)}`;
  const outcome = round.row.outcome;
  if (outcome === null) return head;
  switch (outcome.kind) {
    case "completed": {
      const tools = emittedToolCalls(round.row).map((part) => part.toolName);
      const cached =
        outcome.cachedInputTokens === null
          ? "n/a"
          : formatCount(outcome.cachedInputTokens);
      return `${head}, in ${formatCount(outcome.inputTokens)} (cached ${cached}), out ${formatCount(outcome.outputTokens)}, prompt ${formatBytes(round.row.promptBytes)}, ${formatCredits(outcome.credits)}, ${outcome.finishReason}${tools.length === 0 ? "" : `, calls ${tools.join(" ")}`}`;
    }
    case "failed":
      return `${head}, failed: ${firstLine(outcome.error)}`;
    default:
      throw new Error(outcome satisfies never);
  }
}

function callLine(call: TraceCall): string {
  const exit =
    call.row.exitCode === null ? "" : `, exit ${String(call.row.exitCode)}`;
  const output =
    call.row.outputChars === null
      ? ""
      : `, ${formatCount(call.row.outputChars)} chars`;
  const search =
    call.row.search === null
      ? ""
      : `, ${searchOutcomeText(call.row.search)}, ${formatCredits(callCredits(call.row))}`;
  return `\`${firstLine(call.row.command).slice(0, 120)}\` ${formatMs(call.end - call.start)}${stateText(call.state)}${exit}${output}${search}`;
}

// A compact Markdown account of the run, sized for an LLM prompt.
export function summaryMarkdown(debug: RunDebug, trace: Trace): string {
  const { run } = debug;
  const cached =
    run.cachedInputTokens === null
      ? "n/a"
      : `${formatCount(run.cachedInputTokens)} (${run.inputTokens === 0 ? "n/a" : formatPercent(run.cachedInputTokens / run.inputTokens)})`;
  const lines = [
    `# Assistant run ${run.id}`,
    "",
    `- status ${run.status}, intent ${run.intent === null ? "n/a" : run.intent}, attempt ${String(run.attempt)}`,
    `- duration ${formatMs(trace.end)}, queue ${trace.queue === null ? "n/a" : formatMs(trace.queue)}, cost ${formatCredits(run.credits)}`,
    `- ${String(debug.rounds.length)} rounds, tokens in ${formatCount(run.inputTokens)}, cached ${cached}, out ${formatCount(run.outputTokens)}, reasoning ${run.reasoningTokens === null ? "n/a" : formatCount(run.reasoningTokens)}`,
    `- model time ${formatMs(trace.modelMs)}, tool time ${formatMs(trace.toolMs)}, ${String(debug.calls.length)} tool calls, ${String(trace.sources)} sources`,
  ];
  if (debug.failure !== null) lines.push("", "## Failure", "", debug.failure);
  lines.push("", "## Agents");
  for (const agent of trace.agents) {
    const wait =
      agent.wait === null
        ? ""
        : `, waited ${formatMs(agent.wait.end - agent.wait.start)}`;
    lines.push(
      "",
      `### ${agent.label}: ${formatMs(agent.start)} to ${formatMs(agent.end)}, model ${formatMs(agent.modelMs)}, tools ${formatMs(agent.toolMs)}, ${formatCredits(agent.credits)}${wait}`,
      ""
    );
    for (const round of agent.rounds) {
      lines.push(`- ${roundLine(round)}`);
      for (const call of round.calls) lines.push(`  - ${callLine(call)}`);
    }
    for (const call of agent.calls) lines.push(`- ${callLine(call)}`);
  }
  if (trace.alerts.length > 0) {
    lines.push("", "## Alerts", "");
    for (const { alert, where } of trace.alerts)
      lines.push(`- [${alertCategory(alert)}] ${where}: ${alertText(alert)}`);
  }
  return lines.join("\n");
}
